/**
 * Exact-phrase-first search (Gallery Pop).
 *
 * Shopify's built-in search always blends in "semantically related" products, so a search
 * like `Sad Salesman` also returns unrelated blind boxes. Shopify's own search syntax turns
 * that off for one query: a quoted phrase only matches products that contain the phrase.
 *
 * Rule, applied to every form marked `data-phrase-search` (search page + predictive search):
 *   - Two or more words, no search syntax typed by the shopper → send the query as "a phrase".
 *     Shopify filters the whole catalog server-side, so pagination, filters and sorting
 *     stay correct (nothing is re-sorted in the browser).
 *   - If that phrase finds nothing (partial words like "Sad Sales", typos), Liquid renders a
 *     `[data-phrase-fallback]` marker and this module re-runs the same query normally.
 *   - Single words ("Kona", "Negora") are never changed, so prefix matching keeps working.
 * The shopper always sees their own words in the search box, never the quotes.
 */
const STORAGE_KEY = 'sc-phrase-off';
const SYNTAX = /["*:()]|\b(AND|OR|NOT)\b/;
const FORM_SELECTOR = 'form[data-phrase-search]';

const normalize = (value) => String(value || '').trim().replace(/\s+/g, ' ').toLowerCase();

let phraseOff;
try {
  phraseOff = new Set(JSON.parse(sessionStorage.getItem(STORAGE_KEY) || '[]'));
} catch {
  phraseOff = new Set();
}

function rememberFallback(query) {
  phraseOff.add(normalize(query));
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify([...phraseOff].slice(-50)));
  } catch {
    /* storage unavailable: the fallback still works for this page */
  }
}

function unquote(value) {
  const trimmed = String(value || '').trim();
  return /^".+"$/.test(trimmed) ? trimmed.slice(1, -1) : value;
}

function shouldPhrase(query) {
  const trimmed = String(query || '').trim();
  if (!trimmed || SYNTAX.test(trimmed)) return false;
  if (trimmed.split(/\s+/).length < 2) return false;
  return !phraseOff.has(normalize(trimmed));
}

function bindForm(form) {
  if (form.dataset.phraseBound) return;
  form.dataset.phraseBound = 'true';
  // `formdata` fires for native GET submits and for s-live-form's `new FormData(form)`.
  form.addEventListener('formdata', (event) => {
    const query = event.formData.get('q');
    if (shouldPhrase(query)) event.formData.set('q', `"${String(query).trim()}"`);
  });
}

function showShopperWords() {
  for (const input of document.querySelectorAll(`${FORM_SELECTOR} input[name="q"]`)) {
    const plain = unquote(input.value);
    if (plain !== input.value) input.value = plain;
  }
}

function formForMarker(marker) {
  const kind = marker.closest('#predictive-search-listbox') ? 'predictive' : 'page';
  return document.querySelector(`form[data-phrase-search="${kind}"]`);
}

function handleMarker(marker) {
  if (marker.dataset.handled) return;
  marker.dataset.handled = 'true';
  const form = formForMarker(marker);
  const input = form?.querySelector('input[name="q"]');
  if (!input) return;
  const query = unquote(input.value);
  if (!query.trim() || phraseOff.has(normalize(query))) return;
  rememberFallback(query);
  // Re-run through the form's own debounced live update (never a native navigation).
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

function handleFullPageFallback() {
  const marker = document.querySelector('[data-phrase-fallback]:not(#predictive-search-listbox [data-phrase-fallback])');
  if (!marker) return false;
  const url = new URL(window.location.href);
  const query = url.searchParams.get('q');
  if (!query || unquote(query) === query) return false;
  rememberFallback(unquote(query));
  url.searchParams.set('q', unquote(query));
  url.searchParams.delete('page');
  window.location.replace(url.toString());
  return true;
}

document.querySelectorAll(FORM_SELECTOR).forEach(bindForm);

if (!handleFullPageFallback()) {
  // s-live-form restores the search box from the URL when it upgrades; show plain words after that.
  customElements.whenDefined('s-live-form').then(() => requestAnimationFrame(showShopperWords));
  showShopperWords();
}

new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) {
      if (!(node instanceof Element)) continue;
      if (node.matches(FORM_SELECTOR)) bindForm(node);
      node.querySelectorAll?.(FORM_SELECTOR).forEach(bindForm);
      const markers = node.matches('[data-phrase-fallback]') ? [node] : [...node.querySelectorAll('[data-phrase-fallback]')];
      markers.forEach(handleMarker);
    }
  }
}).observe(document.documentElement, { childList: true, subtree: true });
